import { Box, Typography } from '@mui/material';

import { formatEntityId, formatSecondsToHHMMRounded } from '../../../helpers';
import {
  FORTA_BLUE,
  FORTA_DOCS,
  FORTA_ERROR,
  FORTA_GRAY1,
  FORTA_WHITE,
} from '../../../utils/colors';
import { ENTITY_PREFIX } from '../../../constants';
import { cardChipStyles } from '../constants';
import { FHTooltip } from '../../../components/base';
import { getCSNTimeRequiredTooltipLabel } from '../utils';

interface ActionRequiredCardProps {
  id: number;
  clientName: string;
  session_duration: number;
  client_supervision_duration: number;
  additional_csn_duration_required: number;
}

function CSNToReviewCard({
  id,
  clientName,
  session_duration,
  client_supervision_duration,
  additional_csn_duration_required,
}: ActionRequiredCardProps) {
  const additionalHoursNeeded = formatSecondsToHHMMRounded(additional_csn_duration_required);

  const clientSNHours = formatSecondsToHHMMRounded(client_supervision_duration);
  const sessionHours = formatSecondsToHHMMRounded(session_duration);

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: FORTA_WHITE,
        borderRadius: '8px',
        mb: 1,
        mr: 1,
        border: `1px solid ${FORTA_BLUE.light1}`,
        '&:hover': {
          boxShadow: `1px 2px 4px rgba(0, 0, 0, 0.2)`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography textAlign="left" fontWeight={600} color={FORTA_BLUE.dark} mr={2}>
          {clientName}
        </Typography>
        <Typography sx={{ ...cardChipStyles }}>
          {formatEntityId(id, ENTITY_PREFIX.Client)}
        </Typography>
      </Box>
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            mt: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <FHTooltip
              sx={{ inset: 'auto auto 6px 0px' }}
              title={'Client supervision (97155)'}
              arrow
              placement="top"
              maxwidth={360}
              tooltipBorderRadius="8px"
              tooltipbackground={FORTA_GRAY1}
              tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
            >
              <Box
                sx={{
                  ...cardChipStyles,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: FORTA_DOCS.CSN_55,
                    mr: 0.5,
                  }}
                />
                <Typography fontWeight={300} fontSize={14}>
                  {`${clientSNHours}`}
                </Typography>
              </Box>
            </FHTooltip>
            <FHTooltip
              sx={{ inset: 'auto auto 6px 0px' }}
              title={'Sessions (97153)'}
              arrow
              placement="top"
              maxwidth={360}
              tooltipBorderRadius="8px"
              tooltipbackground={FORTA_GRAY1}
              tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
            >
              <Box sx={{ ...cardChipStyles, display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: FORTA_DOCS.SN_53,
                    mr: 0.5,
                  }}
                />
                <Typography fontWeight={300} fontSize={14}>
                  {`${sessionHours}`}
                </Typography>
              </Box>
            </FHTooltip>
          </Box>

          <FHTooltip
            sx={{ inset: 'auto auto 6px 0px' }}
            title={`${getCSNTimeRequiredTooltipLabel(additional_csn_duration_required)}`}
            arrow
            placement="top"
            maxwidth={220}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_GRAY1}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <Box>
              <Typography
                sx={{
                  ...cardChipStyles,
                  fontWeight: 400,
                  backgroundColor: FORTA_ERROR,
                  color: FORTA_WHITE,
                }}
              >
                {`${additionalHoursNeeded} needed`}
              </Typography>
            </Box>
          </FHTooltip>
        </Box>
      </Box>
    </Box>
  );
}

export default CSNToReviewCard;
